import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../shared/api/axios';
import type { Project } from './types';
import ProjectEditorModal from './ProjectEditorModal';
import ProjectListTable from './ProjectListTable';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function ProjectsPage() {
    const [items, setItems] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [q, setQ] = useState('');
    const [openCreator, setOpenCreator] = useState(false);
    const [openEditor, setOpenEditor] = useState(false);
    const [editing, setEditing] = useState<Project | null>(null);

    const { me, logout } = useAuth();
    const nav = useNavigate();

    const filtered = useMemo(() => {
        const k = q.trim().toLowerCase();
        if (!k) return items;
        return items.filter((p) => p.name.toLowerCase().includes(k) || (p.code ?? '').toLowerCase().includes(k));
    }, [q, items]);

    // const reload = async () => {
    //     setErr(null);
    //     setLoading(true);
    //     try {
    //         const r = await api.get('/projects'); // { ok, data } or array
    //         setItems(r.data?.data ?? r.data ?? []);
    //     } catch (e: any) {
    //         setErr(e?.response?.data?.error?.message ?? '프로젝트 목록을 불러올 수 없습니다.');
    //     } finally {
    //         setLoading(false);
    //     }
    // };
    const reload = async () => {
        setErr(null);
        setLoading(true);
        try {
            const r = await api.get('/projects');
            const base: Project[] = r.data?.data ?? r.data ?? [];

            // ✅ weekly summary (ProjectList는 날짜 선택이 없으니 asOf 생략 = today)
            const s = await api.get('/projects/weekly-progress-summary');
            const rows: Array<{
                projectId: string;
                weekOf: string;
                plannedPct: number;
                actualPct: number;
                status: string;
            }> = s.data?.data ?? s.data ?? [];

            const map = new Map(rows.map((x) => [x.projectId, x]));

            const merged = base.map((p) => {
                const w = map.get(p.id);
                if (!w) return p;
                return {
                    ...p,
                    plannedProgressCached: w.plannedPct,
                    actualProgressCached: w.actualPct,
                    progressStatusCached: w.status as any,
                };
            });

            setItems(merged);
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? '프로젝트 목록을 불러올 수 없습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void reload();
    }, []);

    const canCreateProject = me?.isCompanyAdmin;

    return (
        <div className="w-full //max-w-6xl mx-auto p-6">
            {/* 헤더 (AllProjectsDashboard 느낌) */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-xl font-semibold">내 프로젝트</h1>
                    <div className="text-xs text-gray-500">총 {items.length}개</div>
                </div>

                {/* ⬇ 오른쪽: 계정 아이콘 영역 */}
                <div className="flex items-center gap-2">
                    {me ? (
                        <>
                            {/* 사용자 전환/프로필 (간단 표시) */}
                            <button
                                className="inline-flex items-center rounded-xl border px-3 py-2 text-xs //hover:bg-gray-50"
                                title="사용자 정보"
                                // onClick={() => nav('/projects/overview')}
                                disabled
                            >
                                <span className="mr-2">👤</span>
                                {me.name ?? '사용자'}
                            </button>
                            {/* 로그아웃 */}
                            <button
                                className="inline-flex items-center rounded-xl border px-3 py-2 text-xs hover:bg-gray-50"
                                title="로그아웃"
                                onClick={() => {
                                    if (typeof logout === 'function') {
                                        logout();
                                    }
                                    nav('/login?logged_out=1', { replace: true });
                                }}
                            >
                                <span className="mr-2">🚪</span> 로그아웃
                            </button>
                        </>
                    ) : (
                        <button
                            className="inline-flex items-center rounded-xl border px-3 py-2 text-xs hover:bg-gray-50"
                            title="로그인"
                            onClick={() => nav('/login')}
                        >
                            <span className="mr-2">🔑</span> 로그인
                        </button>
                    )}
                </div>
            </div>

            {/* 검색바 */}
            <div className="mb-3 flex items-center justify-between gap-3">
                {/* ⬅ 왼쪽: 검색 */}
                <input
                    className="w-full text-xs md:w-96 rounded-xl border px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
                    placeholder="프로젝트명/코드 검색"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />

                {/* ➡ 오른쪽: 버튼 그룹 */}
                <div className="shrink-0 flex items-center gap-2">
                    <Link
                        to="/projects/overview"
                        className="inline-flex items-center rounded-xl border px-3 py-2 text-xs hover:bg-gray-50"
                    >
                        프로젝트 모아보기
                    </Link>
                    {canCreateProject && (
                        <button
                            className="rounded-xl bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-xs font-medium"
                            onClick={() => setOpenCreator(true)}
                        >
                            프로젝트 생성
                        </button>
                    )}
                </div>
            </div>

            {/* 카드 + 테이블 */}
            <div className="rounded-2xl border bg-white overflow-hidden">
                <div className="px-4 py-3 border-b text-sm font-medium">Projects</div>
                <div className="p-0 overflow-x-auto">
                    {loading ? (
                        <div className="p-4">로딩중…</div>
                    ) : err ? (
                        <div className="p-4 text-sm text-red-600">{err}</div>
                    ) : filtered.length === 0 ? (
                        <div className="p-4 text-sm text-gray-600">프로젝트가 없습니다.</div>
                    ) : (
                        <ProjectListTable
                            items={filtered}
                            loading={loading}
                            err={err}
                            onEdit={(p) => {
                                if (!p?.id) {
                                    setErr('선택한 프로젝트에 id가 없습니다.');
                                    return;
                                }
                                setEditing(p);
                                setOpenEditor(true);
                            }}
                        />
                    )}
                </div>
            </div>

            {/* 프로젝트 생성 모달 */}
            <ProjectEditorModal
                open={openCreator}
                mode="create"
                onClose={() => setOpenCreator(false)}
                onSaved={async () => {
                    setOpenCreator(false);
                    await reload();
                }}
            />

            {/* 프로젝트 수정 모달 */}
            <ProjectEditorModal
                open={openEditor}
                mode="edit"
                projectId={editing?.id}
                initial={editing ?? undefined}
                onClose={() => {
                    setOpenEditor(false);
                    setEditing(null);
                }}
                onSaved={async () => {
                    setOpenEditor(false);
                    setEditing(null);
                    await reload();
                }}
            />
        </div>
    );
}
